import { eachDayOfInterval } from 'date-fns'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '../../data/app-parameters'
import { SubscriptionPlan } from '../../types/company'
import { FeatureFlagEnum } from '../../types/feature-flags'
import { DayOfWeek, IUserWorkingHours, IWorkingSchedule, WorkingHours } from '../../types/work-week'
import { IGetLocation, IGetLocationGeneral, IGetLocationsShort } from '../../../frontend/src/types/locations'
import { IHoliday } from '../../types/holidays'
import dayjs, { Dayjs } from 'dayjs'

export const HOURS_MIN = 1
export const HOURS_MAX = 23
export const HOURS_STEP = 0.5

export function userHasDefaultWorkingHours(workHours?: WorkingHours | IWorkingSchedule) {
  if (!workHours) {
    return true
  }
  if (!Array.isArray(workHours)) {
    return false // If workHours are set as rotating schedule, we don't have default working hours
  }
  return workHours.every((workDay) => Number(workDay[1]) === DEFAULT_WORKING_HOURS_IN_DAY)
}

export function getHalfDayDurationInMinutes(workingHoursOnDay: number): number {
  const hours = workingHoursOnDay > 0 ? workingHoursOnDay : DEFAULT_WORKING_HOURS_IN_DAY
  return Math.round((hours * 60) / 2)
}

export function getUserWorkingHoursOnDay(workHours: WorkingHours | IWorkingSchedule | undefined, date: Date): number {
  if (!workHours || isNaN(date?.getTime())) {
    return 0
  }

  // Use dayjs to handle the timezone conversion properly
  const localDate = dayjs(dayjs(date).toISOString().split('T')[0])

  // case when user has only one week of working hours schedule:
  if (Array.isArray(workHours)) {
    const workDay = workHours?.find((workDay) => workDay[0] === localDate.day())
    return workDay ? Number(workDay[1]) : 0
  }

  // case when user has rotating working hours schedule:
  const { workHours: scheduleHours, startDate } = workHours

  // If no startDate, use first week's schedule
  if (!startDate) {
    return getUserWorkingHoursOnDay(scheduleHours[0], localDate.toDate())
  }

  // Calculate which week's schedule to use
  const startDateObj = dayjs(startDate).startOf('day')
  const targetDate = localDate
  // If it's Sunday, use previous day to calculate the week
  const adjustedTargetDate = targetDate.day() === 0 ? targetDate.subtract(1, 'day') : targetDate

  // Get the start of the week for both dates
  const getStartOfWeek = (date: Dayjs) => {
    return date.startOf('week')
  }

  const startWeek = getStartOfWeek(startDateObj)
  const targetWeek = getStartOfWeek(adjustedTargetDate)

  const weeksDiff = Math.floor(targetWeek.diff(startWeek, 'week'))

  // Handle negative weeks (before start date) by adding schedule length until positive
  let adjustedWeeksDiff = weeksDiff
  while (adjustedWeeksDiff < 0) {
    adjustedWeeksDiff += scheduleHours.length
  }

  const weekIndex = adjustedWeeksDiff % scheduleHours.length
  // Get hours for the specific day using the correct week's schedule
  const weekSchedule = scheduleHours[weekIndex]
  const workDay = weekSchedule.find(([scheduleDay]) => scheduleDay === localDate.day())

  return workDay ? Number(workDay[1]) : 0
}

export function getAverageWorkingHoursRounded(workHours?: WorkingHours | IWorkingSchedule): number {
  // sometimes average working hours is not divisible by 0.5 or 1, so we need to round it to the nearest 0.5
  // ex user works 11hours on Monday, 11 hours on Tuesday and 11.5 hours on Wednesday
  const averageWorkingHours = getAverageWorkingHours(workHours)
  return Math.round(averageWorkingHours * 2) / 2
}

export function getAverageWorkingHours(workHours?: WorkingHours | IWorkingSchedule): number {
  if (!workHours) {
    return 0
  }
  // case when user has rotating working hours schedule:
  if (!Array.isArray(workHours)) {
    const allHours = workHours.workHours
      .flat()
      .map(([, hours]) => hours)
      .filter((hours) => hours > 0)

    if (allHours.length === 0) return 0

    const sum = allHours.reduce((acc, hours) => acc + hours, 0)
    return sum / allHours.length
  }

  if (workHours.length === 0) {
    return 0
  }
  // case when user has only one week of working hours schedule:
  const workHoursFiltered = workHours.filter((workDay) => Number(workDay[1]) > 0) // Filter out days with 0 hours
  return workHoursFiltered.reduce((total, [, hours]) => total + hours, 0) / workHoursFiltered.length
}

// Location can have no work hours set, so we need to return a default value
export function getAverageWorkingHoursForLocation(locationWorkHours?: WorkingHours): number {
  return getAverageWorkingHours(locationWorkHours) || DEFAULT_WORKING_HOURS_IN_DAY
}

export function isWorkDayFeatureAllowed(featureFlags: string[], plan?: SubscriptionPlan) {
  return featureFlags?.includes(FeatureFlagEnum.workday) || plan === 'Complete'
}

export function isUserWorkHoursFeatureAllowed(featureFlags: string[], plan: SubscriptionPlan) {
  return featureFlags?.includes(FeatureFlagEnum.qaMode) || plan === 'Complete'
}
// user work hours schedule feature is allowed only for hourly leave accounting, whereas single user work hours is allowed for all leave accounting types
export function isUserWorkHoursScheduleFeatureAllowed(featureFlags: string[], hourlyLeaveAccounting: boolean, plan: SubscriptionPlan) {
  return hourlyLeaveAccounting && isUserWorkHoursFeatureAllowed(featureFlags, plan)
}

export function countWorkingHoursBetweenDates(start: Date, end: Date, workingHours: WorkingHours | IWorkingSchedule): number {
  return eachDayOfInterval({ start, end }).reduce((acc, date) => {
    return acc + getUserWorkingHoursOnDay(workingHours, date)
  }, 0)
}

// Used for toil requests which can be made on non working days - so total earned hours from toil should consider non working days as well
export function countAllHoursBetweenDates(start: Date, end: Date, workingHours: WorkingHours | IWorkingSchedule): number {
  return eachDayOfInterval({ start, end }).reduce((acc, date) => {
    let workingHoursInDay = getUserWorkingHoursOnDay(workingHours, date)
    if (workingHoursInDay === 0) {
      // if user has no working hours set for that day, we consider it as a working day and count average working hours
      // users with custom working hours are not allowed to request multi day toils anyway
      workingHoursInDay = getAverageWorkingHours(workingHours)
    }
    return acc + workingHoursInDay
  }, 0)
}

// User can have either flat hours schedule(changing each day) or rotating schedule(changing each week)
// No schedule means that user has same work hours each day each week
export function userHasWorkingHoursSchedule(userWorkingHours?: WorkingHours | IWorkingSchedule): boolean {
  if (!userWorkingHours) {
    return false
  }
  if (userHasRotatingSchedule(userWorkingHours)) {
    return true // case when user has rotating working hours schedule:
  }
  if ((userWorkingHours as WorkingHours).length < 2) {
    // We need at least 2 days to have a schedule
    return false
  }
  return (userWorkingHours as WorkingHours).some((workingHours) => workingHours[1] !== (userWorkingHours as WorkingHours)[0][1])
}

export function userHasRotatingSchedule(userWorkingHours?: WorkingHours | IWorkingSchedule | null): boolean {
  return !!userWorkingHours && 'workHours' in userWorkingHours
}

export function getLocationWorkingHours(location: IGetLocationGeneral | IGetLocation | IGetLocationsShort): WorkingHours {
  if (location && location.workHours && location.workHours.length > 0) {
    return location.workHours
  }
  return location.workWeek.map((day) => [day as DayOfWeek, DEFAULT_WORKING_HOURS_IN_DAY]) as WorkingHours
}

// This function is used to get the start and end dates of a week in a rotating working hours schedule for today's week
// Example: schedule was set as 4 weeks in january and we want to get the start and end dates of the first week of August
export function getRotationDates(workingHours: IWorkingSchedule, weekIndex: number) {
  const startDate = new Date(workingHours.startDate as string)
  const today = new Date()
  const totalWeeks = Math.floor((today.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000))
  const currentPosition = totalWeeks % workingHours.workHours.length

  const currentRotationStart = new Date(today)
  const dayOfWeek = currentRotationStart.getDay()
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  currentRotationStart.setDate(today.getDate() - daysFromMonday - currentPosition * 7)

  const weekStart = new Date(currentRotationStart)
  weekStart.setDate(weekStart.getDate() + weekIndex * 7)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  return { weekStart, weekEnd }
}

export function getWeekStartDateFromDate(firstDayOfTheWeek: number, fromDate: Date = new Date()): Date {
  const dayOfWeek = fromDate.getDay()
  const diff =
    firstDayOfTheWeek === 1
      ? dayOfWeek === 0
        ? -6
        : 1 - dayOfWeek // For Monday
      : -dayOfWeek // For Sunday

  const weekStart = new Date(fromDate)
  weekStart.setDate(fromDate.getDate() + diff)
  return weekStart
}

// User working hours were initially saved in db as single array
// When domain logic changed and we introduced rotating working hours schedule, we needed to save it as array of arrays
// This function is used to return both props(either workHours or schedule will be defined) mainly because of the graphql schema
export function formatUserWorkingHours(userWorkingHours: WorkingHours | IWorkingSchedule | undefined): IUserWorkingHours {
  if (!userWorkingHours) {
    return {
      workHours: undefined,
      schedule: undefined,
    }
  }
  const hasSchedule = 'workHours' in userWorkingHours
  return {
    workHours: hasSchedule ? undefined : (userWorkingHours as WorkingHours),
    schedule: hasSchedule ? (userWorkingHours as IWorkingSchedule) : undefined,
  }
}

// Opposite function over previous one(formatUserWorkingHours()) - it returns either schedule or workHours depending on the type
// Used when we get complex object from graphql (IUserWorkingHours) to return either schedule or flat array of working hours
export function getUserWorkingHoursDependingOnType(workingHours?: IUserWorkingHours): WorkingHours | IWorkingSchedule | null {
  if (!workingHours) {
    return null
  }
  return workingHours.schedule ? workingHours.schedule : workingHours.workHours || null
}

// This function is used to get the nth working day from a given date
// For example for ooo reminders we need first working day from today
// And for schedule reminders we need nth working day depending on reminder attempt(first, second, third)
export function getNthWorkingDayFromDate(workingHours: WorkingHours | IWorkingSchedule, n: number, fromDate = new Date(), holidays?: IHoliday[]): Date {
  const currentDate = fromDate

  const isRotatingSchedule = userHasRotatingSchedule(workingHours)

  if (isRotatingSchedule) {
    const schedule = workingHours as IWorkingSchedule
    const startDate = schedule.startDate ? new Date(schedule.startDate) : new Date()
    let workDaysCount = 0

    while (workDaysCount < n) {
      currentDate.setDate(currentDate.getDate() + 1)
      const currentDay = currentDate.getDay()

      // Calculate which week in the rotation we're on
      const daysDiff = Math.floor((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      const weekIndex = Math.floor(daysDiff / 7) % schedule.workHours.length

      // Check if this day is a working day in the current rotation week
      const isWorkDay = schedule.workHours[weekIndex].some(([day]) => day === currentDay) && !holidays?.some((holiday) => holiday.date === currentDate.toISOString().split('T')[0])
      if (isWorkDay) {
        workDaysCount++
      }
    }
  } else {
    const simpleWorkHours = workingHours as WorkingHours
    const workingDays = simpleWorkHours.map(([day]) => day)

    let workDaysCount = 0
    while (workDaysCount < n) {
      currentDate.setDate(currentDate.getDate() + 1)
      const currentDay = currentDate.getDay()
      if (workingDays.includes(currentDay as DayOfWeek) && !holidays?.some((holiday) => holiday.date === currentDate.toISOString().split('T')[0])) {
        workDaysCount++
      }
    }
  }

  return currentDate
}

// This function is used to get the days of the week for a given working hours schedule
// For rotating schedule, it returns unique days from all weeks
// For regular schedule, it returns all days of the week
export function getWorkWeekDays(workingHours: WorkingHours | IWorkingSchedule): number[] {
  if (userHasRotatingSchedule(workingHours)) {
    // For rotating schedule, get unique days from all weeks
    const schedule = workingHours as IWorkingSchedule
    return [...new Set(schedule.workHours.flat().map(([day]) => day))]
  }
  // For regular schedule, simply map the days
  return (workingHours as WorkingHours).map(([day]) => day)
}
